import { EnumBlockCodeTracking } from 'constants/tracking';
import { CartStore, CrossSellItem, ICartMembershipOffer, Product, ProductQuickOrder, ProductReplace, Seller } from 'types/product';
import { decryptAES, genkey } from 'utils/decode';

export function convertProductQuickOrder(product): ProductQuickOrder {
  const {
    productId,
    slug,
    imageUrl,
    productName,
    skuCode = '',
    sellerId,
    sellerName,
    sellerCode,
    sellerUrl,
    tags = [],
    type,
    maxQuantity = 0,
    discountPercent,
    discountPriceEncrypted,
    priceEncrypted,
    subPriceEncrypted,
    skuReplace = [],
    limitMessage = '',
    quantityInCart = 0,
    isWishlist = false,
    labels = [],
    errorCode,
    errorMessage,
    warningMessage,
    productTags = [],
    isHighProfit = false,
    styleDisplay,
    trackingMetadata = {},
  } = product;

  const keygen = genkey(`${process.env.NEXT_PUBLIC_DECODE_PRICE_KEY}`);
  const currentPrice = decryptAES({ key: keygen, value: priceEncrypted || '' });
  const priceDiscount = decryptAES({ key: keygen, value: discountPriceEncrypted || '' });
  const skuReplaceFormat = skuReplace.map((product) => {
    return {
      ...product,
      price: decryptAES({ key: keygen, value: product.priceEncrypted || '' }),
      // BV-1669: giá gốc để gạch ngang trên card sản phẩm thay thế. BFF ghi đè
      // priceEncrypted bằng giá đã giảm nên phải đọc originPriceEncrypted.
      originPrice: decryptAES({ key: keygen, value: product.originPriceEncrypted || '' }) || 0,
    };
  });

  trackingMetadata.price_display = String(priceDiscount || currentPrice);

  return {
    id: productId,
    name: productName,
    imageUrl,
    tags,
    priceDiscount,
    discountPercent,
    currentPrice,
    quantityInCart,
    maxQuantity,
    limitMessage,
    slug,
    sellerId,
    sellerName,
    sellerUrl,
    sellerCode,
    skuReplace: skuReplaceFormat,
    // BV-2271: BFF chỉ gắn crossSell vào 1 dòng của mỗi SP, dòng khác không có field
    crossSell: Array.isArray(product.crossSell) ? product.crossSell.map(convertCrossSellItem) : undefined,
    skuCode,
    isWishlist,
    isDeal: type === 'DEAL',
    labels,
    errorCode,
    errorMessage,
    warningMessage,
    type,
    productTags,
    isHighProfit,
    styleDisplay,
    trackingMetadata,
    isIngredientSpecial: !!product.isIngredientSpecial,
    sellerCategoryCode: product?.sellerCategoryCode,
    sellerSubCategoryCode: product?.sellerSubCategoryCode,
    drugType: product?.drugType,
    // BV-1080: pass-through eligibility flag từ BFF (Lite shape).
    eligibility: product?.eligibility,
  };
}

function convertProduct(pData): Product {
  const {
    productName,
    productVolume,
    productId,
    priceLabel,
    discountPercent,
    discountDetail,
    priceEncrypted,
    discountPriceEncrypted,
    oldPriceEncrypted,
    maxQuantity,
    quantityInCart,
    imageUrl,
    isImportant,
    isSelected,
    isCanClickable,
    isFluctuated,
    labels,
    tags,
    limitMessage,
    skuCode,
    sellerCode,
    sellerId,
    type,
    errorCode,
    errorMessage,
    fluctuatedType,
    slug,
    nearExpireMessage,
    warningCode,
    warningMessage,
    productCode,
    storeCode,
    productTags,
    noneVoucherPriceEncrypted,
    retailPriceApplyVoucherEncrypt,
    skuReplace,
    // high profit product
    isHighProfit,
    styleDisplay,
    trackingMetadata,
    isFrozen = false,
  } = pData;

  const keygen = genkey(`${process.env.NEXT_PUBLIC_DECODE_PRICE_KEY}`);
  const price = decryptAES({ key: keygen, value: priceEncrypted || '' });
  const discountPrice = decryptAES({ key: keygen, value: discountPriceEncrypted || '' });
  const oldPrice = decryptAES({ key: keygen, value: oldPriceEncrypted || '' });
  const noneVoucherPrice = decryptAES({ key: keygen, value: noneVoucherPriceEncrypted || '' });
  const retailPriceApplyVoucher = decryptAES({ key: keygen, value: retailPriceApplyVoucherEncrypt || '' });

  trackingMetadata.price_display = String(discountPrice || price);

  return {
    id: productId,
    name: productName,
    volume: productVolume,
    price,
    priceLabel,
    discountPrice,
    discountDetail,
    oldPrice: oldPrice || 0,
    discountPercent: discountPercent || 0,
    maxQuantity,
    quantityInCart,
    imageUrl,
    isImportant,
    isSelected: isSelected || false,
    isCanClickable: isCanClickable || false,
    labels: labels || [],
    tags: tags || [],
    limitMessage: limitMessage || '',
    slug,
    skuCode,
    sellerCode: sellerCode || '',
    sellerId: sellerId || '',
    type,
    errorCode,
    errorMessage,
    fluctuatedType,
    nearExpireMessage,
    isFluctuated: isFluctuated || false,
    warningCode,
    warningMessage,
    productCode,
    storeCode,
    productTags: productTags || [],
    noneVoucherPrice,
    retailPriceApplyVoucher,
    skuReplace: skuReplace ? skuReplace.map((item: any) => ({ ...convertCartItemReplace(item) })) : null,
    isHighProfit,
    styleDisplay,
    trackingMetadata,
    isIngredientSpecial: !!pData.isIngredientSpecial,
    isFrozen,
    sellerCategoryCode: pData?.sellerCategoryCode,
    sellerSubCategoryCode: pData?.sellerSubCategoryCode,
    drugType: pData?.drugType,
    // BV-1080: pass-through eligibility flag từ BFF (Lite shape).
    eligibility: pData?.eligibility,
    // BV-1120: cờ combo từ BFF — sku type của combo vẫn là NORMAL nên FE phải
    // dựa cờ này để phân nhánh check business-scope whitelist trong giỏ.
    isCombo: Boolean(pData?.isCombo),
  };
}

export function convertCartItem(product): Seller {
  const { sellerGroup, sellerName, sellerCode, sellerAvatar, sellerUrl, isSelected, items, totalItemDiscount = 0, isCartPriority = false } = product;
  const groupKey = product.groupKey || sellerGroup;

  const getStoreCode = Array.from(new Set(items?.map((item: any) => item?.storeCode)))?.[0] as string;

  return {
    products: items.map((item: any) => ({ ...convertProduct(item), sellerGroup, groupKey })),
    code: sellerCode || '',
    name: sellerName,
    imageUrl: sellerAvatar,
    sellerGroup,
    groupKey,
    isCartPriority,
    isSelected: isSelected || false,
    slug: sellerUrl || '',
    totalDiscount: totalItemDiscount,
    storeCode: getStoreCode || '',
  };
}

export function convertCartStore(cart): CartStore {
  const {
    redeemApplyResult = [],
    fluctuatedItems = [],
    errorItems = [],
    discount = 0,
    totalItem = 0,
    totalItemSelected = 0,
    totalQuantity,
    totalQuantitySelected = 0,
    totalPriceEncrypted,
    totalImportantItem = 0,
    priceEncrypted,
    subPriceEncrypted,
    isSelected,
    paymentMethod,
    hasDeal = false,
    membershipOffer,
    membershipFee = 0,
  } = cart;

  const cartStoreItems: Seller[] = (cart.cartItemGroups || []).map(convertCartItem);

  const keygen = genkey(`${process.env.NEXT_PUBLIC_DECODE_PRICE_KEY}`);
  const totalPrice = decryptAES({ key: keygen, value: totalPriceEncrypted || '' }) || 0;
  const subPrice = decryptAES({ key: keygen, value: subPriceEncrypted || '' }) || 0;
  const price = decryptAES({ key: keygen, value: priceEncrypted || '' }) || 0;

  const membershipOfferDecrypted: ICartMembershipOffer | undefined = membershipOffer && {
    type: membershipOffer.type,
    name: membershipOffer.name,
    shortName: membershipOffer.shortName,
    discountPercent: membershipOffer.discountPercent,
    discountValue: decryptAES({ key: keygen, value: membershipOffer.discountValueEncrypt || '' }) || 0,
    subPrice: decryptAES({ key: keygen, value: membershipOffer.subPriceEncrypt || '' }) || 0,
  };

  const selectedProducts = cartStoreItems.reduce((pre: Product[], cur) => {
    const newProduct = cur.products.filter((product) => product.isSelected);
    pre.push(...newProduct);
    return pre;
  }, []);

  return {
    cartNo: cart.cartNo,
    cartData: cartStoreItems,
    cartDiscounts: redeemApplyResult,
    cartFluctuates: fluctuatedItems.map((item: any) => convertProduct(item)),
    cartErrors: errorItems.map((item: any) => convertProduct(item)),
    selectedProducts,
    isOpenImportantProducts: false,
    isSelected: isSelected || false,
    totalDiscount: discount,
    totalItems: totalItem,
    totalItemsSelected: totalItemSelected,
    totalPrice,
    totalQuantity,
    totalQuantitySelected,
    totalItemsImportantSelected: totalImportantItem,
    price,
    paymentMethod,
    redeemApplyResult,
    subPrice,
    membershipFee,
    membershipOffer: membershipOfferDecrypted,
    hasDeal,
  };
}

export function convertCartItemReplace(itemRelace): ProductReplace {
  const { imageUrl = '', priceEncrypted, productId, productName, slug, volume, retailPriceApplyVoucherEncrypt, originPriceEncrypted } = itemRelace;

  const keygen = genkey(`${process.env.NEXT_PUBLIC_DECODE_PRICE_KEY}`);
  const price = decryptAES({ key: keygen, value: retailPriceApplyVoucherEncrypt || priceEncrypted || '' }) || 0;
  // BV-1669: giá gốc để gạch ngang trên card sản phẩm thay thế. BFF ghi đè
  // priceEncrypted bằng giá đã giảm nên phải đọc originPriceEncrypted.
  const originPrice = decryptAES({ key: keygen, value: originPriceEncrypted || '' }) || 0;

  return {
    ...itemRelace,
    imageUrl,
    productId,
    productName,
    slug,
    volume,
    price,
    originPrice,
  };
}

// BV-2271: item "SP hay mua kèm" dùng chung bộ giải mã giá với skuReplace (spread giữ nguyên sellerName/sellerUrl);
// blockCode để add-to-cart gửi metadata block_code (useCartQuantityControl đọc product.blockCode)
export function convertCrossSellItem(item): CrossSellItem {
  return { ...convertCartItemReplace(item), blockCode: EnumBlockCodeTracking.CROSS_SELL_PRODUCT };
}
